import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchProjectCategories,
  fetchProjectCategory,
  updateProjectCategory,
  createProjectCategory,
  deleteProjectCategory,
} from "../api/projectCategories";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteProjectCategoryAsync = createAsyncThunk(
  "projectCategories/deleteProjectCategoryAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteProjectCategory(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_project_category");
    }
  }
);

export const createProjectCategoryAsync = createAsyncThunk(
  "projectCategories/createProjectCategoryAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createProjectCategory(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "create_project_category"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_project_category");
    }
  }
);

export const updateProjectCategoryAsync = createAsyncThunk(
  "projectCategories/updateProjectCategoryAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProjectCategory(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "update_project_category"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_project_category");
    }
  }
);

export const getProjectCategoriesAsync = createAsyncThunk(
  "projectCategories/getProjectCategoriesAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchProjectCategories(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "list_project_categories"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_project_categories");
    }
  }
);

export const getProjectCategoryAsync = createAsyncThunk(
  "projectCategories/getProjectCategoryAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchProjectCategory(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_project_category");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_project_category");
    }
  }
);

const projectCategoriesSlice = createSlice({
  name: "projectCategories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProjectCategoriesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getProjectCategoriesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newestProjectCategoriesCount = action.payload.newCount;
      })
      .addCase(getProjectCategoriesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente prosjektkategoriene."
        );
      })
      .addCase(updateProjectCategoryAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateProjectCategoryAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload?.id
          );
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateProjectCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere prosjektkategorien."
        );
      })
      .addCase(createProjectCategoryAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createProjectCategoryAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(createProjectCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette prosjektkategorien."
        );
      })
      .addCase(deleteProjectCategoryAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteProjectCategoryAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = draftState.data.filter(
            (item) => item.id !== action.meta.arg.id
          );
          draftState.isLoading = false;
        });
      })
      .addCase(deleteProjectCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette prosjektkategorien."
        );
      })
      .addCase(getProjectCategoryAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getProjectCategoryAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.projectCategory = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getProjectCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente prosjektkategorien."
        );
      });
  },
});

export const { reducer } = projectCategoriesSlice;

export default projectCategoriesSlice;
